import { Extension, Mark, mergeAttributes } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';

const SAFE_LINK_PROTOCOL = /^(https?:\/\/|mailto:)/i;

const normaliseLinkHref = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (SAFE_LINK_PROTOCOL.test(trimmed)) return trimmed;
  if (/^www\./i.test(trimmed)) return `https://${trimmed}`;
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(?:[/:?#].*)?$/i.test(trimmed)) return `https://${trimmed}`;
  return '';
};

// Lightweight hyperlink mark for the existing editor. This deliberately avoids
// adding another package dependency while supporting selection, paste and
// automatic URL linking as the user types.
export const Hyperlink = Mark.create({
  name: 'hyperlink',
  inclusive: false,

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: element => normaliseLinkHref(element.getAttribute('href')),
        renderHTML: attributes => ({ href: attributes.href }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[href]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setHyperlink: href => ({ chain }) => {
        const safeHref = normaliseLinkHref(href);
        if (!safeHref) return false;
        return chain().setMark(this.name, { href: safeHref }).run();
      },
      unsetHyperlink: () => ({ chain }) => chain().unsetMark(this.name).run(),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some(transaction => transaction.docChanged)) return null;

          let transaction = newState.tr;
          let changed = false;
          const urlPattern = /https?:\/\/[^\s<]+/gi;

          newState.doc.descendants((node, pos) => {
            if (!node.isText || !node.text) return;
            const parent = newState.doc.resolve(pos).parent;
            if (parent.type.name === 'codeBlock') return;

            urlPattern.lastIndex = 0;
            let match;
            while ((match = urlPattern.exec(node.text)) !== null) {
              let url = match[0];
              const trailing = url.match(/[.,!?;:)}\]]+$/);
              if (trailing) url = url.slice(0, -trailing[0].length);
              if (!url) continue;

              const from = pos + match.index;
              const to = from + url.length;
              const alreadyLinked = node.marks.some(mark => mark.type.name === this.name && mark.attrs.href === normaliseLinkHref(url));
              if (!alreadyLinked) {
                transaction.addMark(from, to, this.type.create({ href: normaliseLinkHref(url) }));
                changed = true;
              }
            }
          });

          return changed ? transaction : null;
        },
      }),
    ];
  },
});


// Custom Font Size Extension
export const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize }).run();
      },
      unsetFontSize: () => ({ chain }) => {
        return chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
      },
    };
  },
});

// Custom Line Height Extension applied to paragraphs and headings
export const LineHeight = Extension.create({
  name: 'lineHeight',
  addOptions() {
    return {
      types: ['paragraph', 'heading', 'list_item'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: element => element.style.lineHeight.replace(/['"]+/g, ''),
            renderHTML: attributes => {
              if (!attributes.lineHeight) return {};
              return { style: `line-height: ${attributes.lineHeight}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setLineHeight: lineHeight => ({ commands }) => {
        return this.options.types.every(type => commands.updateAttributes(type, { lineHeight }));
      },
      unsetLineHeight: () => ({ commands }) => {
        return this.options.types.every(type => commands.resetAttributes(type, 'lineHeight'));
      },
    };
  },
});